import { QRCodeData, SMSData, EventData, WiFiData, VCardData } from '../types';
import QRCode from 'qrcode';

export const generateQRContent = (data: QRCodeData, additionalData?: any): string => {
  switch (data.type) {
    case 'url':
      return data.content.startsWith('http') ? data.content : `https://${data.content}`;
    
    case 'email':
      return `mailto:${data.content}`;
    
    case 'phone':
      return `tel:${data.content}`;
    
    case 'sms':
      const smsData = additionalData as SMSData;
      return `SMSTO:${smsData.phone}:${smsData.message}`;
    
    case 'wifi':
      const wifiData = additionalData as WiFiData;
      return `WIFI:T:${wifiData.encryption};S:${wifiData.ssid};P:${wifiData.password};H:${wifiData.hidden ? 'true' : 'false'};;`;
    
    case 'vcard':
      const vcardData = additionalData as VCardData;
      return `BEGIN:VCARD
VERSION:3.0
N:${vcardData.lastName};${vcardData.firstName}
FN:${vcardData.firstName} ${vcardData.lastName}
${vcardData.organization ? `ORG:${vcardData.organization}` : ''}
${vcardData.title ? `TITLE:${vcardData.title}` : ''}
${vcardData.phone ? `TEL:${vcardData.phone}` : ''}
${vcardData.email ? `EMAIL:${vcardData.email}` : ''}
${vcardData.website ? `URL:${vcardData.website}` : ''}
${vcardData.address ? `ADR:${vcardData.address}` : ''}
END:VCARD`;
    
    case 'event':
      const eventData = additionalData as EventData;
      return `BEGIN:VEVENT
SUMMARY:${eventData.title}
LOCATION:${eventData.location}
DTSTART:${eventData.startDate.replace(/[-:]/g, '')}
DTEND:${eventData.endDate.replace(/[-:]/g, '')}
DESCRIPTION:${eventData.description}
END:VEVENT`;
    
    case 'text':
    default:
      return data.content;
  }
};

export const validateQRContent = (content: string, type: QRCodeData['type']): boolean => {
  if (!content.trim()) return false;

  switch (type) {
    case 'url':
      try {
        new URL(content.startsWith('http') ? content : `https://${content}`);
        return true;
      } catch {
        return false;
      }
    
    case 'email':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(content);
    
    case 'phone':
      const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
      return phoneRegex.test(content.replace(/[\s\-()]/g, ''));
    
    case 'sms':
    case 'wifi':
    case 'vcard':
    case 'event':
    case 'text':
      return content.length > 0;
    
    default:
      return false;
  }
};

export const generateShortCode = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export const formatQRType = (type: QRCodeData['type']): string => {
  const typeMap = {
    url: 'Website URL',
    text: 'Plain Text',
    email: 'Email Address',
    phone: 'Phone Number',
    sms: 'SMS Message',
    wifi: 'WiFi Network',
    vcard: 'Contact Card',
    event: 'Calendar Event',
  };
  return typeMap[type] || 'Unknown';
};

export const getQRTypeIcon = (type: QRCodeData['type']): string => {
  const iconMap = {
    url: '🌐',
    text: '📝',
    email: '📧',
    phone: '📱',
    sms: '💬',
    wifi: '📶',
    vcard: '👤',
    event: '📅',
  };
  return iconMap[type] || '❓';
};

export const downloadQRCode = (canvas: HTMLCanvasElement, filename: string = 'qrcode.png') => {
  const link = document.createElement('a');
  link.download = filename;
  link.href = canvas.toDataURL();
  link.click();
};

// SVG Download Function
export const downloadQRCodeSVG = async (
  content: string,
  customization: any,
  filename: string = 'qrcode.svg'
) => {
  try {
    const svg = await QRCode.toString(content, {
      type: 'svg',
      width: customization.size,
      color: {
        dark: customization.foregroundColor,
        light: customization.transparentBackground ? 'transparent' : customization.backgroundColor,
      },
      errorCorrectionLevel: customization.errorCorrectionLevel,
      margin: customization.marginSize,
    });

    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = filename;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating SVG:', error);
  }
};

// PNG with Transparent Background
export const downloadQRCodePNG = async (
  content: string,
  customization: any,
  transparent: boolean = false,
  filename: string = 'qrcode.png'
) => {
  try {
    const canvas = document.createElement('canvas');
    await QRCode.toCanvas(canvas, content, {
      width: customization.size,
      color: {
        dark: customization.foregroundColor,
        light: transparent || customization.transparentBackground ? '#00000000' : customization.backgroundColor,
      },
      errorCorrectionLevel: customization.errorCorrectionLevel,
      margin: customization.marginSize,
    });

    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = filename;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      }
    });
  } catch (error) {
    console.error('Error generating PNG:', error);
  }
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy text: ', err);
    return false;
  }
};

// Generate shareable link
export const generateShareableLink = (qrType: string, content: string, customization?: any): string => {
  const baseUrl = window.location.origin;
  const params = new URLSearchParams({
    type: qrType,
    data: content,
    ...(customization && {
      fg: customization.foregroundColor?.replace('#', ''),
      bg: customization.backgroundColor?.replace('#', ''),
      size: customization.size?.toString(),
      level: customization.errorCorrectionLevel,
    }),
  });
  return `${baseUrl}/generate?${params.toString()}`;
};
